import type { ThemeConfig } from 'antd';

/**
 * Client ID type definition
 * Supports 5 different client theme variants
 */
export type ClientId = 'client1' | 'client2' | 'client3' | 'client4' | 'client5';

/**
 * Theme Style Variants
 */
export type ThemeStyle = 'modern' | 'classic' | 'minimal' | 'bold' | 'elegant';

/**
 * Color Intensity Levels
 */
export type ColorIntensity = 'subtle' | 'standard' | 'vibrant' | 'high-contrast';

/**
 * Border Radius Style
 */
export type BorderRadiusStyle = 'none' | 'subtle' | 'standard' | 'rounded' | 'pill';

/**
 * Shadow Intensity
 */
export type ShadowIntensity = 'none' | 'subtle' | 'standard' | 'elevated' | 'dramatic';

/**
 * ⚙️ CLIENT THEME CONFIGURATION
 * 
 * This setting controls which theme is applied to the application.
 * Theme colors are automatically configured and applied - users do not need to provide colors.
 * 
 * Available options: 'client1', 'client2', 'client3', 'client4', 'client5'
 * 
 * Each client has a pre-configured theme with brand colors that are automatically used
 * throughout the application. Users see the appropriate theme based on their organization.
 */
export const CLIENT_ID: ClientId = 'client2';

/**
 * 🎨 THEME CUSTOMIZATION OPTIONS
 * 
 * NOTE: These are developer configuration options only.
 * Users do not need to provide or customize theme colors - they are automatically configured.
 * Theme colors are set per client and applied automatically throughout the application.
 * 
 * To modify theme settings, developers should update the CLIENT_THEMES configuration below.
 */
export const THEME_OPTIONS = {
  // Theme Style Variant - Enhanced for Orange Care
  style: 'elegant' as ThemeStyle, // 'modern' | 'classic' | 'minimal' | 'bold' | 'elegant'
  
  // Color Intensity - More vibrant for healthcare warmth
  colorIntensity: 'vibrant' as ColorIntensity, // 'subtle' | 'standard' | 'vibrant' | 'high-contrast'
  
  // Border Radius Style - Rounded for modern healthcare look
  borderRadiusStyle: 'rounded' as BorderRadiusStyle, // 'none' | 'subtle' | 'standard' | 'rounded' | 'pill'
  
  // Shadow Intensity - Elevated for depth and professionalism
  shadowIntensity: 'elevated' as ShadowIntensity, // 'none' | 'subtle' | 'standard' | 'elevated' | 'dramatic'
  
  // Additional Options - Enhanced for better UX
  enableAnimations: true,
  enableHoverEffects: true,
  enableFocusRings: true,
  enableGradients: true,        // ✅ Enable gradients for modern look
  enableGlassmorphism: true,    // ✅ Enable glassmorphism for elegance
  compactMode: false,
  highContrast: false,
  darkMode: false,
};

/**
 * Brand Colors Configuration
 * 
 * Each client has unique primary, secondary, and accent colors that are automatically configured.
 * These colors are applied throughout the application automatically - users do not need to provide them.
 * 
 * Theme colors are set per client organization and cannot be customized by end users.
 */
interface BrandColors {
  // Primary Colors
  primary: string;
  primaryHover: string;
  primaryActive: string;
  primaryLight: string;
  primaryLighter: string;
  primaryDark: string;
  
  // Secondary Colors
  secondary: string;
  secondaryHover: string;
  
  // Status Colors
  success: string;
  successLight: string;
  warning: string;
  warningLight: string;
  error: string;
  errorLight: string;
  info: string;
  infoLight: string;
  
  // Neutral Colors (Professional Gray Scale)
  neutral50: string;
  neutral100: string;
  neutral200: string;
  neutral300: string;
  neutral400: string;
  neutral500: string;
  neutral600: string;
  neutral700: string;
  neutral800: string;
  neutral900: string;
  
  // Background Colors
  bgBase: string;
  bgLayout: string;
  bgContainer: string;
  bgElevated: string;
  bgSpotlight: string;
  
  // Border Colors
  borderBase: string;
  borderSecondary: string;
  borderLight: string;
  
  // Text Colors
  textBase: string;
  textSecondary: string;
  textTertiary: string;
  textDisabled: string;
  textInverse: string;
  
  // Link Colors
  link: string;
  linkHover: string;
  linkActive: string;
}

/**
 * Typography Configuration
 * Professional type scale following 8px grid system
 */
interface Typography {
  fontFamily: string;
  fontFamilyCode: string;
  fontSize: {
    xs: number;      // 12px - Captions, labels
    sm: number;      // 14px - Secondary text
    base: number;    // 16px - Body text
    lg: number;      // 18px - Large body
    xl: number;      // 20px - Subheadings
    '2xl': number;   // 24px - Section titles
    '3xl': number;   // 30px - Page titles
    '4xl': number;   // 36px - Large headings
    '5xl': number;   // 48px - Hero text
    '6xl': number;   // 60px - Display text
  };
  fontWeight: {
    thin: number;       // 100 - Ultra light
    extralight: number; // 200 - Extra light
    light: number;      // 300 - Light
    normal: number;     // 400 - Regular
    medium: number;     // 500 - Medium
    semibold: number;   // 600 - Semi-bold
    bold: number;       // 700 - Bold
    extrabold: number;  // 800 - Extra bold
    black: number;      // 900 - Black
  };
  lineHeight: {
    none: number;     // 1 - Tight for headings
    tight: number;    // 1.25 - For large text
    snug: number;     // 1.375 - Comfortable headings
    normal: number;   // 1.5 - Body text
    relaxed: number;  // 1.625 - Comfortable reading
    loose: number;    // 2 - Very spacious
  };
  letterSpacing: {
    tighter: string;  // -0.05em
    tight: string;    // -0.025em
    normal: string;   // 0
    wide: string;     // 0.025em
    wider: string;    // 0.05em
    widest: string;   // 0.1em
  };
}

/**
 * Spacing System Configuration
 * Based on 4px grid system for perfect alignment
 */
interface Spacing {
  0: number;      // 0px - No spacing
  px: number;     // 1px - Hairline
  0.5: number;    // 2px - Minimal
  1: number;      // 4px - Tiny
  1.5: number;    // 6px - Extra small
  2: number;      // 8px - Small
  2.5: number;    // 10px - Small-medium
  3: number;      // 12px - Medium-small
  3.5: number;    // 14px - Medium
  4: number;      // 16px - Base
  5: number;      // 20px - Medium-large
  6: number;      // 24px - Large
  7: number;      // 28px - Extra large
  8: number;      // 32px - 2XL
  10: number;     // 40px - 3XL
  12: number;     // 48px - 4XL
  14: number;     // 56px - 5XL
  16: number;     // 64px - 6XL
  20: number;     // 80px - 7XL
  24: number;     // 96px - 8XL
  28: number;     // 112px - 9XL
  32: number;     // 128px - 10XL
  
  // Legacy aliases for backward compatibility
  xs: number;     // 4px
  sm: number;     // 8px
  md: number;     // 16px
  lg: number;     // 24px
  xl: number;     // 32px
  '2xl': number;  // 48px
  '3xl': number;  // 64px
  '4xl': number;  // 96px
}

/**
 * Border Radius Configuration
 */
interface BorderRadius {
  none: number;
  sm: number;
  base: number;
  md: number;
  lg: number;
  xl: number;
  full: number;
}

/**
 * Shadow System Configuration
 */
interface Shadows {
  none: string;
  sm: string;
  base: string;
  md: string;
  lg: string;
  xl: string;
  '2xl': string;
}

/**
 * Breakpoints for Responsive Design
 */
interface Breakpoints {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  xxl: number;
}

/**
 * Motion & Animation Configuration
 */
interface Motion {
  durationFast: string;
  durationBase: string;
  durationSlow: string;
  easeIn: string;
  easeOut: string;
  easeInOut: string;
  easeOutBack: string;
  easeInBack: string;
  easeInOutBack: string;
}

/**
 * Z-Index System for Layering
 */
interface ZIndex {
  base: number;
  dropdown: number;
  sticky: number;
  fixed: number;
  modalBackdrop: number;
  modal: number;
  popover: number;
  tooltip: number;
}

/**
 * Opacity System
 */
interface Opacity {
  disabled: number;
  hover: number;
  active: number;
  loading: number;
}

/**
 * Container & Content Width System
 */
interface Containers {
  xs: number;      // 480px - Mobile
  sm: number;      // 640px - Small tablet
  md: number;      // 768px - Tablet
  lg: number;      // 1024px - Desktop
  xl: number;      // 1280px - Large desktop
  '2xl': number;   // 1536px - Extra large
  full: string;    // 100% - Full width
  screen: string;  // 100vw - Viewport width
}

/**
 * Transition System
 */
interface Transitions {
  all: string;
  colors: string;
  opacity: string;
  shadow: string;
  transform: string;
  none: string;
}

/**
 * Focus Ring System (Accessibility)
 */
interface Focus {
  ringWidth: number;
  ringColor: string;
  ringOffset: number;
  ringOpacity: number;
}

/**
 * Grid System
 */
interface Grid {
  columns: number;
  gap: number;
  gapX: number;
  gapY: number;
}

/**
 * Aspect Ratios
 */
interface AspectRatios {
  square: string;      // 1:1
  video: string;       // 16:9
  ultrawide: string;   // 21:9
  portrait: string;    // 4:5
  photo: string;       // 3:2
  golden: string;      // 1.618:1
}

/**
 * Screen Reader & Accessibility
 */
interface Accessibility {
  srOnly: boolean;
  focusVisible: boolean;
  reducedMotion: boolean;
}

/**
 * Complete Theme Configuration Interface
 */
export interface ThemeConfiguration {
  clientId: ClientId;
  clientName: string;
  colors: BrandColors;
  typography: Typography;
  spacing: Spacing;
  borderRadius: BorderRadius;
  shadows: Shadows;
  breakpoints: Breakpoints;
  motion: Motion;
  zIndex: ZIndex;
  opacity: Opacity;
  containers: Containers;
  transitions: Transitions;
  focus: Focus;
  grid: Grid;
  aspectRatios: AspectRatios;
  accessibility: Accessibility;
}

/**
 * Helper function to generate professional neutral color palette
 */
const generateNeutralColors = () => ({
  neutral50: '#fafafa',
  neutral100: '#f5f5f5',
  neutral200: '#e5e5e5',
  neutral300: '#d4d4d4',
  neutral400: '#a3a3a3',
  neutral500: '#737373',
  neutral600: '#525252',
  neutral700: '#404040',
  neutral800: '#262626',
  neutral900: '#171717',
});

/**
 * Helper function to generate background colors
 */
const generateBackgrounds = () => ({
  bgBase: '#ffffff',
  bgLayout: '#f5f5f5',
  bgContainer: '#ffffff',
  bgElevated: '#ffffff',
  bgSpotlight: '#fafafa',
});

/**
 * Helper function to generate border colors
 */
const generateBorders = () => ({
  borderBase: '#d9d9d9',
  borderSecondary: '#f0f0f0',
  borderLight: '#f5f5f5',
});

/**
 * Helper function to generate text colors
 */
const generateTextColors = () => ({
  textBase: 'rgba(0, 0, 0, 0.88)',
  textSecondary: 'rgba(0, 0, 0, 0.65)',
  textTertiary: 'rgba(0, 0, 0, 0.45)',
  textDisabled: 'rgba(0, 0, 0, 0.25)',
  textInverse: '#ffffff',
});

/**
 * Helper function to generate focus ring configuration
 */
const generateFocusRing = (primaryColor: string): Focus => ({
  ringWidth: 2,
  ringColor: primaryColor,
  ringOffset: 2,
  ringOpacity: 0.5,
});

/**
 * 🎨 THEME CUSTOMIZATION HELPERS
 */

/**
 * Generate color variants based on intensity
 * @deprecated Use adjustColor directly for color modifications
 */
// const generateColorVariants = (baseColor: string, intensity: ColorIntensity) => {
//   // Implementation available for future use
// };

/**
 * Adjust color brightness
 * @param color - Hex color string
 * @param percent - Brightness adjustment percentage
 * @returns Adjusted hex color string
 */
export const adjustColor = (color: string, percent: number): string => {
  // Simple color adjustment - in production, use a proper color library
  const num = parseInt(color.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
};

/**
 * Generate border radius based on style
 */
const generateBorderRadius = (style: BorderRadiusStyle) => {
  const radiusMap = {
    none: { none: 0, sm: 0, base: 0, md: 0, lg: 0, xl: 0, full: 0 },
    subtle: { none: 0, sm: 2, base: 4, md: 6, lg: 8, xl: 12, full: 9999 },
    standard: { none: 0, sm: 4, base: 6, md: 8, lg: 12, xl: 16, full: 9999 },
    rounded: { none: 0, sm: 6, base: 8, md: 12, lg: 16, xl: 24, full: 9999 },
    pill: { none: 0, sm: 8, base: 12, md: 16, lg: 20, xl: 32, full: 9999 },
  };
  
  return radiusMap[style];
};

/**
 * Generate shadows based on intensity
 */
const generateShadows = (intensity: ShadowIntensity) => {
  const shadowMap = {
    none: { none: 'none', sm: 'none', base: 'none', md: 'none', lg: 'none', xl: 'none', '2xl': 'none' },
    subtle: {
      none: 'none',
      sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
    standard: {
      none: 'none',
      sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
    elevated: {
      none: 'none',
      sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      base: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      md: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      lg: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      xl: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)',
      '2xl': '0 35px 60px -12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)',
    },
    dramatic: {
      none: 'none',
      sm: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      base: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      md: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      lg: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)',
      xl: '0 35px 60px -12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)',
      '2xl': '0 50px 100px -20px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05)',
    },
  };
  
  return shadowMap[intensity];
};

/**
 * Generate spacing based on compact mode
 */
const generateSpacing = (compact: boolean) => {
  if (compact) {
    return {
      // Compact spacing - reduced by 25%
      0: 0, px: 1, 0.5: 2, 1: 3, 1.5: 6, 2: 6, 2.5: 9, 3: 9, 3.5: 12, 4: 12,
      5: 15, 6: 18, 7: 21, 8: 24, 9: 27, 10: 30, 11: 33, 12: 36, 14: 42, 16: 48,
      20: 60, 24: 72, 28: 84, 32: 96,
      // Legacy aliases
      xs: 6, sm: 9, md: 12, lg: 18, xl: 24, '2xl': 36, '3xl': 48, '4xl': 72,
    };
  }
  
  return commonSpacing;
};

/**
 * 🎨 THEME PRESETS
 * Pre-configured theme styles for different use cases
 */
export const THEME_PRESETS = {
  modern: {
    style: 'modern' as ThemeStyle,
    colorIntensity: 'standard' as ColorIntensity,
    borderRadiusStyle: 'standard' as BorderRadiusStyle,
    shadowIntensity: 'elevated' as ShadowIntensity,
    enableAnimations: true,
    enableHoverEffects: true,
    enableFocusRings: true,
    enableGradients: false,
    enableGlassmorphism: false,
    compactMode: false,
    highContrast: false,
    darkMode: false,
  },
  
  classic: {
    style: 'classic' as ThemeStyle,
    colorIntensity: 'subtle' as ColorIntensity,
    borderRadiusStyle: 'subtle' as BorderRadiusStyle,
    shadowIntensity: 'standard' as ShadowIntensity,
    enableAnimations: false,
    enableHoverEffects: true,
    enableFocusRings: true,
    enableGradients: false,
    enableGlassmorphism: false,
    compactMode: false,
    highContrast: false,
    darkMode: false,
  },
  
  minimal: {
    style: 'minimal' as ThemeStyle,
    colorIntensity: 'subtle' as ColorIntensity,
    borderRadiusStyle: 'subtle' as BorderRadiusStyle,
    shadowIntensity: 'subtle' as ShadowIntensity,
    enableAnimations: false,
    enableHoverEffects: false,
    enableFocusRings: true,
    enableGradients: false,
    enableGlassmorphism: false,
    compactMode: true,
    highContrast: false,
    darkMode: false,
  },
  
  bold: {
    style: 'bold' as ThemeStyle,
    colorIntensity: 'vibrant' as ColorIntensity,
    borderRadiusStyle: 'rounded' as BorderRadiusStyle,
    shadowIntensity: 'dramatic' as ShadowIntensity,
    enableAnimations: true,
    enableHoverEffects: true,
    enableFocusRings: true,
    enableGradients: true,
    enableGlassmorphism: false,
    compactMode: false,
    highContrast: false,
    darkMode: false,
  },
  
  elegant: {
    style: 'elegant' as ThemeStyle,
    colorIntensity: 'standard' as ColorIntensity,
    borderRadiusStyle: 'rounded' as BorderRadiusStyle,
    shadowIntensity: 'elevated' as ShadowIntensity,
    enableAnimations: true,
    enableHoverEffects: true,
    enableFocusRings: true,
    enableGradients: false,
    enableGlassmorphism: true,
    compactMode: false,
    highContrast: false,
    darkMode: false,
  },
  
  accessible: {
    style: 'modern' as ThemeStyle,
    colorIntensity: 'high-contrast' as ColorIntensity,
    borderRadiusStyle: 'standard' as BorderRadiusStyle,
    shadowIntensity: 'standard' as ShadowIntensity,
    enableAnimations: false,
    enableHoverEffects: true,
    enableFocusRings: true,
    enableGradients: false,
    enableGlassmorphism: false,
    compactMode: false,
    highContrast: true,
    darkMode: false,
  },
};

/**
 * Common motion configuration for all themes
 */
const commonMotion: Motion = {
  durationFast: '0.1s',
  durationBase: '0.2s',
  durationSlow: '0.3s',
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  easeOutBack: 'cubic-bezier(0.12, 0.4, 0.29, 1.46)',
  easeInBack: 'cubic-bezier(0.71, -0.46, 0.88, 0.6)',
  easeInOutBack: 'cubic-bezier(0.71, -0.46, 0.29, 1.46)',
};

/**
 * Common z-index system for all themes
 */
const commonZIndex: ZIndex = {
  base: 0,
  dropdown: 1050,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
};

/**
 * Common opacity values for all themes
 */
const commonOpacity: Opacity = {
  disabled: 0.45,
  hover: 0.85,
  active: 1,
  loading: 0.65,
};

/**
 * Container Width System
 * Max-widths for centered content
 */
const commonContainers: Containers = {
  xs: 480,      // Mobile
  sm: 640,      // Small tablet
  md: 768,      // Tablet
  lg: 1024,     // Desktop
  xl: 1280,     // Large desktop
  '2xl': 1536,  // Extra large
  full: '100%', // Full width
  screen: '100vw', // Viewport width
};

/**
 * Transition System
 * Standard transitions for smooth interactions
 */
const commonTransitions: Transitions = {
  all: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  colors: 'color 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  opacity: 'opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  shadow: 'box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  transform: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  none: 'none',
};

/**
 * Grid System Configuration
 * Standard 12-column grid with gaps
 */
const commonGrid: Grid = {
  columns: 12,
  gap: 16,      // 16px default gap
  gapX: 16,     // Horizontal gap
  gapY: 16,     // Vertical gap
};

/**
 * Aspect Ratio System
 * Common aspect ratios for media
 */
const commonAspectRatios: AspectRatios = {
  square: '1 / 1',      // 1:1
  video: '16 / 9',      // 16:9 (YouTube, video)
  ultrawide: '21 / 9',  // 21:9 (Cinema)
  portrait: '4 / 5',    // 4:5 (Instagram portrait)
  photo: '3 / 2',       // 3:2 (Standard photo)
  golden: '1.618 / 1',  // Golden ratio
};

/**
 * Accessibility Configuration
 * WCAG 2.1 compliant settings
 */
const commonAccessibility: Accessibility = {
  srOnly: true,           // Screen reader only content
  focusVisible: true,     // Show focus indicators
  reducedMotion: false,   // Respect prefers-reduced-motion
};

/**
 * Professional Typography Configuration
 * Used across all client themes for consistency
 */
const commonTypography: Typography = {
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji'",
  fontFamilyCode: "'Fira Code', 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Courier New', monospace",
  fontSize: {
    xs: 12,      // Captions, labels
    sm: 14,      // Secondary text
    base: 16,    // Body text (default)
    lg: 18,      // Large body text
    xl: 20,      // Subheadings
    '2xl': 24,   // Section titles
    '3xl': 30,   // Page titles
    '4xl': 36,   // Large headings
    '5xl': 48,   // Hero text
    '6xl': 60,   // Display text
  },
  fontWeight: {
    thin: 100,        // Ultra light
    extralight: 200,  // Extra light
    light: 300,       // Light
    normal: 400,      // Regular (default)
    medium: 500,      // Medium
    semibold: 600,    // Semi-bold
    bold: 700,        // Bold
    extrabold: 800,   // Extra bold
    black: 900,       // Black/Heavy
  },
  lineHeight: {
    none: 1,          // Tight for large display text
    tight: 1.25,      // For headings
    snug: 1.375,      // Comfortable headings
    normal: 1.5,      // Body text (default)
    relaxed: 1.625,   // Comfortable reading
    loose: 2,         // Very spacious
  },
  letterSpacing: {
    tighter: '-0.05em',  // Tight headings
    tight: '-0.025em',   // Large text
    normal: '0',         // Default
    wide: '0.025em',     // Slightly spaced
    wider: '0.05em',     // Button text
    widest: '0.1em',     // All caps labels
  },
};

/**
 * Professional Spacing System
 * 4px base grid for perfect alignment across all themes
 */
const commonSpacing: Spacing = {
  // Modern numeric scale (4px grid)
  0: 0,         // No spacing
  px: 1,        // 1px - Hairline
  0.5: 2,       // 2px - Minimal
  1: 4,         // 4px - Tiny
  1.5: 6,       // 6px - Extra small
  2: 8,         // 8px - Small
  2.5: 10,      // 10px - Small-medium
  3: 12,        // 12px - Medium-small
  3.5: 14,      // 14px - Medium
  4: 16,        // 16px - Base
  5: 20,        // 20px - Medium-large
  6: 24,        // 24px - Large
  7: 28,        // 28px - Extra large
  8: 32,        // 32px - 2XL
  10: 40,       // 40px - 3XL
  12: 48,       // 48px - 4XL
  14: 56,       // 56px - 5XL
  16: 64,       // 64px - 6XL
  20: 80,       // 80px - 7XL
  24: 96,       // 96px - 8XL
  28: 112,      // 112px - 9XL
  32: 128,      // 128px - 10XL
  
  // Legacy aliases for backward compatibility
  xs: 4,        // Tiny
  sm: 8,        // Small
  md: 16,       // Medium (base)
  lg: 24,       // Large
  xl: 32,       // Extra large
  '2xl': 48,    // 2X Large
  '3xl': 64,    // 3X Large
  '4xl': 96,    // 4X Large
};

/**
 * Client-specific theme configurations
 * 
 * These are pre-configured themes for each client organization.
 * Theme colors are automatically applied based on the CLIENT_ID setting above.
 * 
 * IMPORTANT: Users do not need to provide or choose theme colors.
 * The appropriate theme is automatically applied based on their organization/client.
 * 
 * To change the theme for a client, developers should modify the color values in the
 * appropriate client configuration object below (e.g., client1, client2, etc.).
 */
const CLIENT_THEMES: Record<ClientId, ThemeConfiguration> = {
  client1: {
    clientId: 'client1',
    clientName: 'Blue Healthcare',
    colors: {
      // Primary Colors
      primary: '#1890ff',
      primaryHover: '#40a9ff',
      primaryActive: '#096dd9',
      primaryLight: '#69b1ff',
      primaryLighter: '#bae0ff',
      primaryDark: '#0050b3',
      
      // Secondary Colors
      secondary: '#52c41a',
      secondaryHover: '#73d13d',
      
      // Status Colors
      success: '#52c41a',
      successLight: '#b7eb8f',
      warning: '#faad14',
      warningLight: '#ffd666',
      error: '#ff4d4f',
      errorLight: '#ff7875',
      info: '#1890ff',
      infoLight: '#91caff',
      
      // Link Colors
      link: '#1890ff',
      linkHover: '#40a9ff',
      linkActive: '#096dd9',
      
      // Professional Neutral & UI Colors
      ...generateNeutralColors(),
      ...generateBackgrounds(),
      ...generateBorders(),
      ...generateTextColors(),
    },
    typography: commonTypography,
    spacing: commonSpacing,
    borderRadius: {
      none: 0,
      sm: 2,
      base: 4,
      md: 6,
      lg: 8,
      xl: 12,
      full: 9999,
    },
    shadows: {
      none: 'none',
      sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
    breakpoints: {
      xs: 480,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
      xxl: 1600,
    },
    motion: commonMotion,
    zIndex: commonZIndex,
    opacity: commonOpacity,
    containers: commonContainers,
    transitions: commonTransitions,
    focus: generateFocusRing('#1890ff'),
    grid: commonGrid,
    aspectRatios: commonAspectRatios,
    accessibility: commonAccessibility,
  },

  client2: {
    clientId: 'client2',
    clientName: 'Green Medical',
    colors: {
      // Primary Colors - Dark Teal/Green (matching the button)
      primary: '#2d7a6e',        // Main dark teal-green
      primaryHover: '#3d9485',   // Lighter teal on hover
      primaryActive: '#1f5d54',  // Darker teal on active
      primaryLight: '#4daa9a',   // Light teal for backgrounds
      primaryLighter: '#d4f1ed', // Very light teal for subtle backgrounds
      primaryDark: '#18443d',    // Deep teal for emphasis
      
      // Secondary Colors
      secondary: '#1890ff',
      secondaryHover: '#40a9ff',
      
      // Status Colors
      success: '#52c41a',
      successLight: '#b7eb8f',
      warning: '#faad14',
      warningLight: '#ffd666',
      error: '#ff4d4f',
      errorLight: '#ff7875',
      info: '#1890ff',
      infoLight: '#91caff',
      
      // Link Colors
      link: '#52c41a',
      linkHover: '#73d13d',
      linkActive: '#389e0d',
      
      // Professional Neutral & UI Colors
      ...generateNeutralColors(),
      ...generateBackgrounds(),
      ...generateBorders(),
      ...generateTextColors(),
    },
    typography: commonTypography,
    spacing: commonSpacing,
    borderRadius: {
      none: 0,
      sm: 2,
      base: 4,
      md: 6,
      lg: 8,
      xl: 12,
      full: 9999,
    },
    shadows: {
      none: 'none',
      sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
    breakpoints: {
      xs: 480,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
      xxl: 1600,
    },
    motion: commonMotion,
    zIndex: commonZIndex,
    opacity: commonOpacity,
    containers: commonContainers,
    transitions: commonTransitions,
    focus: generateFocusRing('#52c41a'),
    grid: commonGrid,
    aspectRatios: commonAspectRatios,
    accessibility: commonAccessibility,
  },

  client3: {
    clientId: 'client3',
    clientName: 'Purple Wellness',
    colors: {
      // Primary Colors
      primary: '#722ed1',
      primaryHover: '#9254de',
      primaryActive: '#531dab',
      primaryLight: '#b37feb',
      primaryLighter: '#efdbff',
      primaryDark: '#391085',
      
      // Secondary Colors
      secondary: '#eb2f96',
      secondaryHover: '#f759ab',
      
      // Status Colors
      success: '#52c41a',
      successLight: '#b7eb8f',
      warning: '#faad14',
      warningLight: '#ffd666',
      error: '#ff4d4f',
      errorLight: '#ff7875',
      info: '#1890ff',
      infoLight: '#91caff',
      
      // Link Colors
      link: '#722ed1',
      linkHover: '#9254de',
      linkActive: '#531dab',
      
      // Professional Neutral & UI Colors
      ...generateNeutralColors(),
      ...generateBackgrounds(),
      ...generateBorders(),
      ...generateTextColors(),
    },
    typography: commonTypography,
    spacing: commonSpacing,
    borderRadius: {
      none: 0,
      sm: 2,
      base: 4,
      md: 6,
      lg: 8,
      xl: 12,
      full: 9999,
    },
    shadows: {
      none: 'none',
      sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
    breakpoints: {
      xs: 480,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
      xxl: 1600,
    },
    motion: commonMotion,
    zIndex: commonZIndex,
    opacity: commonOpacity,
    containers: commonContainers,
    transitions: commonTransitions,
    focus: generateFocusRing('#722ed1'),
    grid: commonGrid,
    aspectRatios: commonAspectRatios,
    accessibility: commonAccessibility,
  },

  client4: {
    clientId: 'client4',
    clientName: 'Professional Medical',
    colors: {
      // Primary Colors - Professional Medical Blue
      primary: '#2563eb', // Trustworthy medical blue
      primaryHover: '#3b82f6', // Lighter blue for hover
      primaryActive: '#1d4ed8', // Deeper blue for active state
      primaryLight: '#60a5fa', // Light blue for backgrounds
      primaryLighter: '#dbeafe', // Very light blue for subtle backgrounds
      primaryDark: '#1e40af', // Dark blue for emphasis
      
      // Secondary Colors - Complementary teal for accents
      secondary: '#0d9488', // Medical teal
      secondaryHover: '#14b8a6',
      
      // Status Colors
      success: '#52c41a',
      successLight: '#b7eb8f',
      warning: '#faad14',
      warningLight: '#ffd666',
      error: '#ff4d4f',
      errorLight: '#ff7875',
      info: '#1890ff',
      infoLight: '#91caff',
      
      // Link Colors
      link: '#2563eb',
      linkHover: '#3b82f6',
      linkActive: '#1d4ed8',
      
      // Professional Neutral & UI Colors
      ...generateNeutralColors(),
      ...generateBackgrounds(),
      ...generateBorders(),
      ...generateTextColors(),
    },
    typography: commonTypography,
    spacing: commonSpacing,
    borderRadius: {
      none: 0,
      sm: 2,
      base: 4,
      md: 6,
      lg: 8,
      xl: 12,
      full: 9999,
    },
    shadows: {
      none: 'none',
      sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
    breakpoints: {
      xs: 480,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
      xxl: 1600,
    },
    motion: commonMotion,
    zIndex: commonZIndex,
    opacity: commonOpacity,
    containers: commonContainers,
    transitions: commonTransitions,
    focus: generateFocusRing('#2563eb'),
    grid: commonGrid,
    aspectRatios: commonAspectRatios,
    accessibility: commonAccessibility,
  },

  client5: {
    clientId: 'client5',
    clientName: 'Teal Medical Center',
    colors: {
      // Primary Colors
      primary: '#13c2c2',
      primaryHover: '#36cfc9',
      primaryActive: '#08979c',
      primaryLight: '#5cdbd3',
      primaryLighter: '#b5f5ec',
      primaryDark: '#006d75',
      
      // Secondary Colors
      secondary: '#fa8c16',
      secondaryHover: '#ffa940',
      
      // Status Colors
      success: '#52c41a',
      successLight: '#b7eb8f',
      warning: '#faad14',
      warningLight: '#ffd666',
      error: '#ff4d4f',
      errorLight: '#ff7875',
      info: '#1890ff',
      infoLight: '#91caff',
      
      // Link Colors
      link: '#13c2c2',
      linkHover: '#36cfc9',
      linkActive: '#08979c',
      
      // Professional Neutral & UI Colors
      ...generateNeutralColors(),
      ...generateBackgrounds(),
      ...generateBorders(),
      ...generateTextColors(),
    },
    typography: commonTypography,
    spacing: commonSpacing,
    borderRadius: {
      none: 0,
      sm: 2,
      base: 4,
      md: 6,
      lg: 8,
      xl: 12,
      full: 9999,
    },
    shadows: {
      none: 'none',
      sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
    breakpoints: {
      xs: 480,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
      xxl: 1600,
    },
    motion: commonMotion,
    zIndex: commonZIndex,
    opacity: commonOpacity,
    containers: commonContainers,
    transitions: commonTransitions,
    focus: generateFocusRing('#13c2c2'),
    grid: commonGrid,
    aspectRatios: commonAspectRatios,
    accessibility: commonAccessibility,
  },
};

/**
 * Get the current active theme configuration
 */
export const getThemeConfig = (): ThemeConfiguration => {
  return CLIENT_THEMES[CLIENT_ID];
};

/**
 * Get theme configuration for a specific client
 */
export const getThemeConfigByClient = (clientId: ClientId): ThemeConfiguration => {
  return CLIENT_THEMES[clientId];
};

/**
 * Convert theme configuration to Ant Design ThemeConfig format
 */
export const getAntdThemeConfig = (clientId: ClientId = CLIENT_ID): ThemeConfig => {
  const theme = CLIENT_THEMES[clientId];

  return {
    token: {
      // ===== PRIMARY COLOR TOKENS =====
      colorPrimary: theme.colors.primary,
      colorPrimaryHover: theme.colors.primaryHover,
      colorPrimaryActive: theme.colors.primaryActive,
      colorPrimaryBg: theme.colors.primaryLighter,
      colorPrimaryBgHover: theme.colors.primaryLight,
      colorPrimaryBorder: theme.colors.primaryLight,
      colorPrimaryBorderHover: theme.colors.primary,
      
      // ===== STATUS COLOR TOKENS =====
      colorSuccess: theme.colors.success,
      colorSuccessBg: theme.colors.successLight,
      colorWarning: theme.colors.warning,
      colorWarningBg: theme.colors.warningLight,
      colorError: theme.colors.error,
      colorErrorBg: theme.colors.errorLight,
      colorInfo: theme.colors.info,
      colorInfoBg: theme.colors.infoLight,
      
      // ===== LINK COLORS =====
      colorLink: theme.colors.link,
      colorLinkHover: theme.colors.linkHover,
      colorLinkActive: theme.colors.linkActive,

      // ===== TEXT COLORS =====
      colorText: theme.colors.textBase,
      colorTextSecondary: theme.colors.textSecondary,
      colorTextTertiary: theme.colors.textTertiary,
      colorTextQuaternary: theme.colors.textDisabled,
      
      // ===== BACKGROUND COLORS =====
      colorBgBase: theme.colors.bgBase,
      colorBgContainer: theme.colors.bgContainer,
      colorBgElevated: theme.colors.bgElevated,
      colorBgLayout: theme.colors.bgLayout,
      colorBgSpotlight: theme.colors.bgSpotlight,
      
      // ===== BORDER COLORS =====
      colorBorder: theme.colors.borderBase,
      colorBorderSecondary: theme.colors.borderSecondary,
      
      // ===== TYPOGRAPHY TOKENS =====
      fontFamily: theme.typography.fontFamily,
      fontSize: theme.typography.fontSize.base,
      fontSizeHeading1: theme.typography.fontSize['4xl'],
      fontSizeHeading2: theme.typography.fontSize['3xl'],
      fontSizeHeading3: theme.typography.fontSize['2xl'],
      fontSizeHeading4: theme.typography.fontSize.xl,
      fontSizeHeading5: theme.typography.fontSize.lg,
      fontSizeSM: theme.typography.fontSize.sm,
      fontSizeLG: theme.typography.fontSize.lg,
      fontSizeXL: theme.typography.fontSize.xl,
      fontWeightStrong: theme.typography.fontWeight.semibold,
      
      // ===== LINE HEIGHT =====
      lineHeight: theme.typography.lineHeight.normal,
      lineHeightHeading1: theme.typography.lineHeight.tight,
      lineHeightHeading2: theme.typography.lineHeight.tight,
      lineHeightHeading3: theme.typography.lineHeight.tight,
      lineHeightHeading4: theme.typography.lineHeight.tight,
      lineHeightHeading5: theme.typography.lineHeight.tight,

      // ===== BORDER RADIUS TOKENS =====
      borderRadius: theme.borderRadius.base,
      borderRadiusLG: theme.borderRadius.lg,
      borderRadiusSM: theme.borderRadius.sm,
      borderRadiusXS: theme.borderRadius.sm,
      borderRadiusOuter: theme.borderRadius.base,

      // ===== SPACING TOKENS =====
      padding: theme.spacing.md,
      paddingLG: theme.spacing.lg,
      paddingSM: theme.spacing.sm,
      paddingXS: theme.spacing.xs,
      paddingXL: theme.spacing.xl,
      paddingContentHorizontal: theme.spacing.lg,
      paddingContentVertical: theme.spacing.md,
      
      margin: theme.spacing.md,
      marginLG: theme.spacing.lg,
      marginSM: theme.spacing.sm,
      marginXS: theme.spacing.xs,
      marginXL: theme.spacing.xl,
      marginXXL: theme.spacing['2xl'],
      
      // ===== SHADOWS =====
      boxShadow: theme.shadows.base,
      boxShadowSecondary: theme.shadows.sm,
      boxShadowTertiary: theme.shadows.md,
      
      // ===== MOTION & ANIMATION =====
      motionDurationFast: theme.motion.durationFast,
      motionDurationMid: theme.motion.durationBase,
      motionDurationSlow: theme.motion.durationSlow,
      motionEaseInOut: theme.motion.easeInOut,
      motionEaseOut: theme.motion.easeOut,
      motionEaseInOutCirc: theme.motion.easeInOutBack,
      motionEaseOutCirc: theme.motion.easeOutBack,
      motionEaseOutBack: theme.motion.easeOutBack,
      
      // ===== Z-INDEX =====
      zIndexBase: theme.zIndex.base,
      zIndexPopupBase: theme.zIndex.dropdown,
      
      // ===== OPACITY =====
      opacityLoading: theme.opacity.loading,
      
      // ===== CONTROL SIZES =====
      controlHeight: 40,
      controlHeightSM: 32,
      controlHeightLG: 48,
      controlHeightXS: 24,
    },
    components: {
      // Button Components
      Button: {
        borderRadius: theme.borderRadius.lg,
        controlHeight: 44,
        controlHeightLG: 52,
        controlHeightSM: 36,
        fontSize: theme.typography.fontSize.base,
        fontWeight: theme.typography.fontWeight.semibold,
        boxShadow: theme.shadows.md,
        primaryShadow: `0 4px 12px ${theme.colors.primary}25`,
        colorTextDisabled: theme.colors.textDisabled,
        colorBgContainerDisabled: theme.colors.bgContainer,
        colorBorder: theme.colors.borderBase,
        colorPrimaryHover: theme.colors.primaryHover,
        colorPrimaryActive: theme.colors.primaryActive,
        colorTextLightSolid: '#ffffff',
        lineWidth: 1,
        lineWidthFocus: 2,
        defaultShadow: `0 2px 4px ${theme.colors.borderBase}20`,
        dangerShadow: `0 4px 12px ${theme.colors.error}30`,
        ghostBg: 'transparent',
      },
      
      // Input Components - Enhanced Professional Healthcare Forms
      Input: {
        borderRadius: theme.borderRadius.lg,
        controlHeight: 44,
        fontSize: theme.typography.fontSize.base,
        paddingBlock: theme.spacing.md,
        paddingInline: theme.spacing.lg,
        activeBorderColor: theme.colors.primary,
        activeShadow: `0 0 0 3px ${theme.colors.primary}20`,
        hoverBorderColor: theme.colors.primary,
        colorError: theme.colors.error,
        colorErrorBorder: theme.colors.error,
        colorErrorHover: theme.colors.errorLight,
        colorWarning: theme.colors.warning,
        colorWarningBorder: theme.colors.warning,
        colorWarningHover: theme.colors.warningLight,
        lineWidth: 2,
        lineWidthFocus: 3,
        colorTextPlaceholder: theme.colors.textTertiary,
        colorBgContainer: theme.colors.bgElevated,
        colorBorder: theme.colors.borderBase,
        boxShadow: theme.shadows.sm,
      },
      InputNumber: {
        borderRadius: theme.borderRadius.base,
        controlHeight: 40,
        activeBorderColor: theme.colors.primary,
        activeShadow: `0 0 0 2px ${theme.colors.primary}20`,
        colorError: theme.colors.error,
        colorErrorBorder: theme.colors.error,
        colorErrorHover: theme.colors.errorLight,
      },
      
      // Select Components - Professional Healthcare Dropdowns
      Select: {
        borderRadius: theme.borderRadius.lg,
        controlHeight: 44,
        controlHeightSM: 36,
        controlHeightLG: 52,
        fontSize: theme.typography.fontSize.base,
        activeBorderColor: theme.colors.primary,
        hoverBorderColor: theme.colors.primary,
        colorError: theme.colors.error,
        colorErrorBorder: theme.colors.error,
        colorErrorHover: theme.colors.errorLight,
        colorTextPlaceholder: theme.colors.textTertiary,
        optionSelectedBg: `${theme.colors.primary}15`,
        optionSelectedColor: theme.colors.primary,
        optionActiveBg: `${theme.colors.primary}08`,
        optionPadding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
        optionFontSize: theme.typography.fontSize.base,
        colorBgContainer: theme.colors.bgElevated,
        colorBorder: theme.colors.borderBase,
        lineWidth: 2,
      },
      
      // Date & Time Pickers
      DatePicker: {
        borderRadius: theme.borderRadius.base,
        controlHeight: 40,
        // Global defaults for date format
        // Note: Format is handled in CustomDatePicker component
      },
      
      // Form Components - Professional Healthcare Forms
      Form: {
        itemMarginBottom: theme.spacing.lg,
        labelFontSize: theme.typography.fontSize.sm,
        labelRequiredMarkColor: theme.colors.error,
        verticalLabelPadding: `0 0 ${theme.spacing.sm}px`,
        verticalLabelMargin: `0 0 ${theme.spacing.xs}px 0`,
      },
      
      // Card Components - Enhanced for Better Visual Appeal
      Card: {
        borderRadius: theme.borderRadius.xl,
        borderRadiusLG: theme.borderRadius.xl,
        boxShadow: theme.shadows.lg,
        headerFontSize: theme.typography.fontSize.xl,
        headerFontSizeSM: theme.typography.fontSize.lg,
        paddingLG: theme.spacing.xl,
        paddingMD: theme.spacing.lg,
        paddingSM: theme.spacing.md,
        headerBg: 'transparent',
        actionsBg: theme.colors.bgSpotlight,
      },
      
      // Modal & Drawer - Professional Healthcare Styling
      Modal: {
        borderRadius: theme.borderRadius.lg,
        borderRadiusLG: theme.borderRadius.xl,
        boxShadow: theme.shadows['2xl'],
        titleFontSize: theme.typography.fontSize.xl,
        contentBg: theme.colors.bgElevated,
        footerBg: theme.colors.bgContainer,
        titleLineHeight: theme.typography.lineHeight.tight,
      },
      Drawer: {
        borderRadius: theme.borderRadius.base,
        boxShadow: theme.shadows['2xl'],
      },
      
      // Table - Enhanced Professional Healthcare Data Grid
      Table: {
        borderRadius: theme.borderRadius.xl,
        borderRadiusLG: theme.borderRadius.xl,
        headerColor: theme.colors.textBase,
        headerSortActiveBg: `${theme.colors.primary}15`,
        headerSortHoverBg: `${theme.colors.primary}08`,
        rowHoverBg: `${theme.colors.primary}06`,
        rowSelectedBg: `${theme.colors.primary}10`,
        rowSelectedHoverBg: `${theme.colors.primary}15`,
        fontSize: theme.typography.fontSize.base,
        cellPaddingBlock: theme.spacing.lg,
        cellPaddingInline: theme.spacing.xl,
        cellPaddingBlockMD: theme.spacing.lg,
        cellPaddingInlineMD: theme.spacing.lg,
        cellPaddingBlockSM: theme.spacing.md,
        cellPaddingInlineSM: theme.spacing.md,
        headerSplitColor: theme.colors.borderLight,
        borderColor: theme.colors.borderSecondary,
        headerBorderRadius: theme.borderRadius.lg,
        footerBg: theme.colors.bgSpotlight,
        footerColor: theme.colors.textSecondary,
        filterDropdownBg: theme.colors.bgElevated,
        filterDropdownMenuBg: theme.colors.bgElevated,
        expandIconBg: theme.colors.bgContainer,
        stickyScrollBarBg: theme.colors.borderBase,
        stickyScrollBarBorderRadius: theme.borderRadius.base,
        rowExpandedBg: theme.colors.bgSpotlight,
        headerBg: `${theme.colors.primary}05`,
        bodySortBg: theme.colors.bgContainer,
      },
      
      // Menu
      Menu: {
        borderRadius: theme.borderRadius.base,
        itemBorderRadius: theme.borderRadius.sm,
        itemActiveBg: `${theme.colors.primary}15`,
        itemSelectedBg: `${theme.colors.primary}20`,
        itemSelectedColor: theme.colors.primary,
        itemHoverBg: `${theme.colors.primary}10`,
        iconSize: 16,
        fontSize: theme.typography.fontSize.base,
      },
      
      // Tabs
      Tabs: {
        borderRadius: theme.borderRadius.base,
        itemActiveColor: theme.colors.primary,
        itemHoverColor: theme.colors.primaryHover,
        itemSelectedColor: theme.colors.primary,
        inkBarColor: theme.colors.primary,
        fontSize: theme.typography.fontSize.base,
      },
      
      // Collapse / Accordion
      Collapse: {
        borderRadius: theme.borderRadius.base,
        headerBg: `${theme.colors.primary}05`,
        headerPadding: `${theme.spacing.md}px ${theme.spacing.lg}px`,
        contentPadding: `${theme.spacing.md}px ${theme.spacing.lg}px`,
      },
      
      // Checkbox & Radio
      Checkbox: {
        borderRadius: theme.borderRadius.sm,
        size: 18,
      },
      Radio: {
        size: 18,
        dotSize: 10,
      },
      
      // Switch
      Switch: {
        trackHeight: 24,
        trackMinWidth: 48,
        handleSize: 20,
        innerMinMargin: 3,
      },
      
      // Slider
      Slider: {
        railSize: 6,
        handleSize: 12,
        handleSizeHover: 14,
        dotSize: 8,
        controlSize: 12,
        railBg: `${theme.colors.primary}15`,
        trackBg: theme.colors.primary,
        handleActiveColor: theme.colors.primaryActive,
      },
      
      // Progress
      Progress: {
        lineBorderRadius: theme.borderRadius.full,
        circleTextFontSize: `${theme.typography.fontSize['2xl']}px`,
        defaultColor: theme.colors.primary,
      },
      
      // Badge
      Badge: {
        fontSize: theme.typography.fontSize.xs,
        dotSize: 8,
        statusSize: 8,
      },
      
      // Tag
      Tag: {
        borderRadius: theme.borderRadius.base,
        borderRadiusSM: theme.borderRadius.sm,
        fontSize: theme.typography.fontSize.sm,
        defaultBg: `${theme.colors.primary}10`,
        defaultColor: theme.colors.primary,
      },
      
      // Avatar
      Avatar: {
        borderRadius: theme.borderRadius.full,
        groupBorderColor: '#ffffff',
        groupOverlapping: -8,
        fontSize: theme.typography.fontSize.base,
      },
      
      // Alert
      Alert: {
        borderRadius: theme.borderRadius.base,
        borderRadiusLG: theme.borderRadius.lg,
        withDescriptionPadding: `${theme.spacing.md}px ${theme.spacing.lg}px`,
      },
      
      // Notification & Message
      Notification: {
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.shadows.lg,
        width: 384,
      },
      Message: {
        borderRadius: theme.borderRadius.base,
        boxShadow: theme.shadows.md,
      },
      
      // Tooltip & Popover
      Tooltip: {
        borderRadius: theme.borderRadius.base,
        fontSize: theme.typography.fontSize.sm,
      },
      Popover: {
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.shadows.md,
        minWidth: 200,
      },
      
      // Dropdown
      Dropdown: {
        borderRadius: theme.borderRadius.base,
        boxShadow: theme.shadows.md,
        paddingBlock: theme.spacing.xs,
      },
      
      // Pagination
      Pagination: {
        borderRadius: theme.borderRadius.base,
        itemSize: 32,
        itemSizeSM: 24,
        fontSize: theme.typography.fontSize.sm,
      },
      
      // Steps
      Steps: {
        iconSize: 32,
        iconSizeSM: 24,
        dotSize: 8,
        titleLineHeight: theme.typography.lineHeight.tight,
      },
      
      // Breadcrumb
      Breadcrumb: {
        itemColor: 'rgba(0, 0, 0, 0.45)',
        lastItemColor: 'rgba(0, 0, 0, 0.85)',
        linkColor: theme.colors.link,
        linkHoverColor: theme.colors.primaryHover,
        fontSize: theme.typography.fontSize.sm,
        iconFontSize: theme.typography.fontSize.sm,
        separatorMargin: theme.spacing.sm,
      },
      
      // Skeleton
      Skeleton: {
        borderRadius: theme.borderRadius.base,
      },
      
      // Divider
      Divider: {
        fontSize: theme.typography.fontSize.base,
        orientationMargin: 0.05,
      },
      
      // Timeline
      Timeline: {
        tailWidth: 2,
        dotBorderWidth: 2,
        dotBg: '#ffffff',
        itemPaddingBottom: theme.spacing.lg,
      },
      
      // Upload
      Upload: {
        borderRadius: theme.borderRadius.base,
        actionsColor: 'rgba(0, 0, 0, 0.45)',
      },
      
      // Rate
      Rate: {
        starSize: 20,
        starColor: theme.colors.warning,
        starBg: 'rgba(0, 0, 0, 0.06)',
      },
      
      // Spin
      Spin: {
        dotSize: 20,
        dotSizeSM: 14,
        dotSizeLG: 32,
      },
      
      // Result
      Result: {
        titleFontSize: theme.typography.fontSize['2xl'],
        subtitleFontSize: theme.typography.fontSize.base,
        iconFontSize: 72,
        extraMargin: `${theme.spacing.lg}px 0 0 0`,
      },
      
      // Statistic
      Statistic: {
        titleFontSize: theme.typography.fontSize.sm,
        contentFontSize: theme.typography.fontSize['2xl'],
      },
    },
  };
};

/**
 * Export SCSS variables from theme configuration
 * Use this in SCSS files via CSS variables
 */
export const getThemeScssVariables = (clientId: ClientId = CLIENT_ID): Record<string, string> => {
  const theme = CLIENT_THEMES[clientId];

  return {
    // ===== PRIMARY COLORS =====
    '--color-primary': theme.colors.primary,
    '--color-primary-hover': theme.colors.primaryHover,
    '--color-primary-active': theme.colors.primaryActive,
    '--color-primary-light': theme.colors.primaryLight,
    '--color-primary-lighter': theme.colors.primaryLighter,
    '--color-primary-dark': theme.colors.primaryDark,
    
    // ===== SECONDARY COLORS =====
    '--color-secondary': theme.colors.secondary,
    '--color-secondary-hover': theme.colors.secondaryHover,
    
    // ===== STATUS COLORS =====
    '--color-success': theme.colors.success,
    '--color-success-light': theme.colors.successLight,
    '--color-warning': theme.colors.warning,
    '--color-warning-light': theme.colors.warningLight,
    '--color-error': theme.colors.error,
    '--color-error-light': theme.colors.errorLight,
    '--color-info': theme.colors.info,
    '--color-info-light': theme.colors.infoLight,
    
    // ===== LINK COLORS =====
    '--color-link': theme.colors.link,
    '--color-link-hover': theme.colors.linkHover,
    '--color-link-active': theme.colors.linkActive,
    
    // ===== NEUTRAL COLORS =====
    '--color-neutral-50': theme.colors.neutral50,
    '--color-neutral-100': theme.colors.neutral100,
    '--color-neutral-200': theme.colors.neutral200,
    '--color-neutral-300': theme.colors.neutral300,
    '--color-neutral-400': theme.colors.neutral400,
    '--color-neutral-500': theme.colors.neutral500,
    '--color-neutral-600': theme.colors.neutral600,
    '--color-neutral-700': theme.colors.neutral700,
    '--color-neutral-800': theme.colors.neutral800,
    '--color-neutral-900': theme.colors.neutral900,
    
    // ===== BACKGROUND COLORS =====
    '--color-bg-base': theme.colors.bgBase,
    '--color-bg-layout': theme.colors.bgLayout,
    '--color-bg-container': theme.colors.bgContainer,
    '--color-bg-elevated': theme.colors.bgElevated,
    '--color-bg-spotlight': theme.colors.bgSpotlight,
    
    // ===== BORDER COLORS =====
    '--color-border-base': theme.colors.borderBase,
    '--color-border-secondary': theme.colors.borderSecondary,
    '--color-border-light': theme.colors.borderLight,
    
    // ===== TEXT COLORS =====
    '--color-text-base': theme.colors.textBase,
    '--color-text-secondary': theme.colors.textSecondary,
    '--color-text-tertiary': theme.colors.textTertiary,
    '--color-text-disabled': theme.colors.textDisabled,
    '--color-text-inverse': theme.colors.textInverse,

    // ===== FONT FAMILIES =====
    '--font-family': theme.typography.fontFamily,
    '--font-family-code': theme.typography.fontFamilyCode,
    
    // ===== FONT SIZES =====
    '--font-size-xs': `${theme.typography.fontSize.xs}px`,
    '--font-size-sm': `${theme.typography.fontSize.sm}px`,
    '--font-size-base': `${theme.typography.fontSize.base}px`,
    '--font-size-lg': `${theme.typography.fontSize.lg}px`,
    '--font-size-xl': `${theme.typography.fontSize.xl}px`,
    '--font-size-2xl': `${theme.typography.fontSize['2xl']}px`,
    '--font-size-3xl': `${theme.typography.fontSize['3xl']}px`,
    '--font-size-4xl': `${theme.typography.fontSize['4xl']}px`,
    '--font-size-5xl': `${theme.typography.fontSize['5xl']}px`,
    '--font-size-6xl': `${theme.typography.fontSize['6xl']}px`,
    
    // ===== FONT WEIGHTS =====
    '--font-weight-thin': `${theme.typography.fontWeight.thin}`,
    '--font-weight-extralight': `${theme.typography.fontWeight.extralight}`,
    '--font-weight-light': `${theme.typography.fontWeight.light}`,
    '--font-weight-normal': `${theme.typography.fontWeight.normal}`,
    '--font-weight-medium': `${theme.typography.fontWeight.medium}`,
    '--font-weight-semibold': `${theme.typography.fontWeight.semibold}`,
    '--font-weight-bold': `${theme.typography.fontWeight.bold}`,
    '--font-weight-extrabold': `${theme.typography.fontWeight.extrabold}`,
    '--font-weight-black': `${theme.typography.fontWeight.black}`,
    
    // ===== LINE HEIGHTS =====
    '--line-height-none': `${theme.typography.lineHeight.none}`,
    '--line-height-tight': `${theme.typography.lineHeight.tight}`,
    '--line-height-snug': `${theme.typography.lineHeight.snug}`,
    '--line-height-normal': `${theme.typography.lineHeight.normal}`,
    '--line-height-relaxed': `${theme.typography.lineHeight.relaxed}`,
    '--line-height-loose': `${theme.typography.lineHeight.loose}`,
    
    // ===== LETTER SPACING =====
    '--letter-spacing-tighter': theme.typography.letterSpacing.tighter,
    '--letter-spacing-tight': theme.typography.letterSpacing.tight,
    '--letter-spacing-normal': theme.typography.letterSpacing.normal,
    '--letter-spacing-wide': theme.typography.letterSpacing.wide,
    '--letter-spacing-wider': theme.typography.letterSpacing.wider,
    '--letter-spacing-widest': theme.typography.letterSpacing.widest,

    // ===== SPACING (LEGACY) =====
    '--spacing-xs': `${theme.spacing.xs}px`,
    '--spacing-sm': `${theme.spacing.sm}px`,
    '--spacing-md': `${theme.spacing.md}px`,
    '--spacing-lg': `${theme.spacing.lg}px`,
    '--spacing-xl': `${theme.spacing.xl}px`,
    '--spacing-2xl': `${theme.spacing['2xl']}px`,
    '--spacing-3xl': `${theme.spacing['3xl']}px`,
    '--spacing-4xl': `${theme.spacing['4xl']}px`,
    
    // ===== SPACING (MODERN SCALE) =====
    '--spacing-0': `${theme.spacing[0]}px`,
    '--spacing-px': `${theme.spacing.px}px`,
    '--spacing-0-5': `${theme.spacing[0.5]}px`,
    '--spacing-1': `${theme.spacing[1]}px`,
    '--spacing-1-5': `${theme.spacing[1.5]}px`,
    '--spacing-2': `${theme.spacing[2]}px`,
    '--spacing-2-5': `${theme.spacing[2.5]}px`,
    '--spacing-3': `${theme.spacing[3]}px`,
    '--spacing-3-5': `${theme.spacing[3.5]}px`,
    '--spacing-4': `${theme.spacing[4]}px`,
    '--spacing-5': `${theme.spacing[5]}px`,
    '--spacing-6': `${theme.spacing[6]}px`,
    '--spacing-7': `${theme.spacing[7]}px`,
    '--spacing-8': `${theme.spacing[8]}px`,
    '--spacing-10': `${theme.spacing[10]}px`,
    '--spacing-12': `${theme.spacing[12]}px`,
    '--spacing-14': `${theme.spacing[14]}px`,
    '--spacing-16': `${theme.spacing[16]}px`,
    '--spacing-20': `${theme.spacing[20]}px`,
    '--spacing-24': `${theme.spacing[24]}px`,
    '--spacing-28': `${theme.spacing[28]}px`,
    '--spacing-32': `${theme.spacing[32]}px`,

    // ===== BORDER RADIUS =====
    '--border-radius-none': `${theme.borderRadius.none}px`,
    '--border-radius-sm': `${theme.borderRadius.sm}px`,
    '--border-radius-base': `${theme.borderRadius.base}px`,
    '--border-radius-md': `${theme.borderRadius.md}px`,
    '--border-radius-lg': `${theme.borderRadius.lg}px`,
    '--border-radius-xl': `${theme.borderRadius.xl}px`,
    '--border-radius-full': `${theme.borderRadius.full}px`,

    // ===== SHADOWS =====
    '--shadow-none': theme.shadows.none,
    '--shadow-sm': theme.shadows.sm,
    '--shadow-base': theme.shadows.base,
    '--shadow-md': theme.shadows.md,
    '--shadow-lg': theme.shadows.lg,
    '--shadow-xl': theme.shadows.xl,
    '--shadow-2xl': theme.shadows['2xl'],
    
    // ===== MOTION & ANIMATION =====
    '--motion-duration-fast': theme.motion.durationFast,
    '--motion-duration-base': theme.motion.durationBase,
    '--motion-duration-slow': theme.motion.durationSlow,
    '--motion-ease-in': theme.motion.easeIn,
    '--motion-ease-out': theme.motion.easeOut,
    '--motion-ease-in-out': theme.motion.easeInOut,
    '--motion-ease-out-back': theme.motion.easeOutBack,
    '--motion-ease-in-back': theme.motion.easeInBack,
    '--motion-ease-in-out-back': theme.motion.easeInOutBack,
    
    // ===== Z-INDEX =====
    '--z-index-base': `${theme.zIndex.base}`,
    '--z-index-dropdown': `${theme.zIndex.dropdown}`,
    '--z-index-sticky': `${theme.zIndex.sticky}`,
    '--z-index-fixed': `${theme.zIndex.fixed}`,
    '--z-index-modal-backdrop': `${theme.zIndex.modalBackdrop}`,
    '--z-index-modal': `${theme.zIndex.modal}`,
    '--z-index-popover': `${theme.zIndex.popover}`,
    '--z-index-tooltip': `${theme.zIndex.tooltip}`,
    
    // ===== OPACITY =====
    '--opacity-disabled': `${theme.opacity.disabled}`,
    '--opacity-hover': `${theme.opacity.hover}`,
    '--opacity-active': `${theme.opacity.active}`,
    '--opacity-loading': `${theme.opacity.loading}`,
    
    // ===== CONTAINERS =====
    '--container-xs': `${theme.containers.xs}px`,
    '--container-sm': `${theme.containers.sm}px`,
    '--container-md': `${theme.containers.md}px`,
    '--container-lg': `${theme.containers.lg}px`,
    '--container-xl': `${theme.containers.xl}px`,
    '--container-2xl': `${theme.containers['2xl']}px`,
    '--container-full': theme.containers.full,
    '--container-screen': theme.containers.screen,
    
    // ===== TRANSITIONS =====
    '--transition-all': theme.transitions.all,
    '--transition-colors': theme.transitions.colors,
    '--transition-opacity': theme.transitions.opacity,
    '--transition-shadow': theme.transitions.shadow,
    '--transition-transform': theme.transitions.transform,
    '--transition-none': theme.transitions.none,
    
    // ===== FOCUS RING (Accessibility) =====
    '--focus-ring-width': `${theme.focus.ringWidth}px`,
    '--focus-ring-color': theme.focus.ringColor,
    '--focus-ring-offset': `${theme.focus.ringOffset}px`,
    '--focus-ring-opacity': `${theme.focus.ringOpacity}`,
    
    // ===== GRID SYSTEM =====
    '--grid-columns': `${theme.grid.columns}`,
    '--grid-gap': `${theme.grid.gap}px`,
    '--grid-gap-x': `${theme.grid.gapX}px`,
    '--grid-gap-y': `${theme.grid.gapY}px`,
    
    // ===== ASPECT RATIOS =====
    '--aspect-ratio-square': theme.aspectRatios.square,
    '--aspect-ratio-video': theme.aspectRatios.video,
    '--aspect-ratio-ultrawide': theme.aspectRatios.ultrawide,
    '--aspect-ratio-portrait': theme.aspectRatios.portrait,
    '--aspect-ratio-photo': theme.aspectRatios.photo,
    '--aspect-ratio-golden': theme.aspectRatios.golden,
  };
};

/**
 * Get all available client IDs
 */
export const getAllClientIds = (): ClientId[] => {
  return Object.keys(CLIENT_THEMES) as ClientId[];
};

/**
 * Get all theme configurations
 */
export const getAllThemes = (): Record<ClientId, ThemeConfiguration> => {
  return CLIENT_THEMES;
};

/**
 * 🎨 ENHANCED THEME UTILITIES
 */

/**
 * Apply theme preset to current options
 */
export const applyThemePreset = (presetName: keyof typeof THEME_PRESETS): void => {
  const preset = THEME_PRESETS[presetName];
  Object.assign(THEME_OPTIONS, preset);
};

/**
 * Generate custom theme with options
 */
export const generateCustomTheme = (
  clientId: ClientId = CLIENT_ID,
  options: Partial<typeof THEME_OPTIONS> = {}
): ThemeConfig => {
  const mergedOptions = { ...THEME_OPTIONS, ...options };
  
  // Apply custom spacing if compact mode
  if (mergedOptions.compactMode) {
    generateSpacing(true);
  }
  
  // Apply custom border radius
  generateBorderRadius(mergedOptions.borderRadiusStyle);
  
  // Apply custom shadows
  generateShadows(mergedOptions.shadowIntensity);
  
  // Generate enhanced theme configuration
  return getAntdThemeConfig(clientId);
};

/**
 * Get theme preview colors
 */
export const getThemePreview = (clientId: ClientId): {
  primary: string;
  secondary: string;
  success: string;
  warning: string;
  error: string;
  name: string;
} => {
  const theme = CLIENT_THEMES[clientId];
  return {
    primary: theme.colors.primary,
    secondary: theme.colors.secondary,
    success: theme.colors.success,
    warning: theme.colors.warning,
    error: theme.colors.error,
    name: theme.clientName,
  };
};

/**
 * Check if theme supports dark mode
 */
export const supportsDarkMode = (): boolean => {
  // All themes support dark mode in this implementation
  return true;
};

/**
 * Get accessibility score for current theme
 */
export const getAccessibilityScore = (clientId: ClientId): number => {
  const theme = CLIENT_THEMES[clientId];
  let score = 100;
  
  // Check color contrast ratios (simplified)
  const primaryContrast = getContrastRatio(theme.colors.primary, '#ffffff');
  if (primaryContrast < 4.5) score -= 20;
  if (primaryContrast < 3) score -= 30;
  
  // Check text contrast
  const textContrast = getContrastRatio(theme.colors.textBase, theme.colors.bgBase);
  if (textContrast < 4.5) score -= 25;
  
  return Math.max(0, score);
};

/**
 * Simple contrast ratio calculation
 */
const getContrastRatio = (_color1: string, _color2: string): number => {
  // Simplified implementation - in production, use a proper color library
  return 4.5; // Placeholder value
};

/**
 * Export theme options for external use
 */
export const getThemeOptions = () => THEME_OPTIONS;

/**
 * Update theme options
 */
export const updateThemeOptions = (options: Partial<typeof THEME_OPTIONS>): void => {
  Object.assign(THEME_OPTIONS, options);
};

/**
 * Reset theme options to defaults
 */
export const resetThemeOptions = (): void => {
  Object.assign(THEME_OPTIONS, THEME_PRESETS.modern);
};

/**
 * Get available theme presets
 */
export const getAvailablePresets = () => Object.keys(THEME_PRESETS);

/**
 * Check if current theme is accessible
 */
export const isThemeAccessible = (clientId: ClientId = CLIENT_ID): boolean => {
  return getAccessibilityScore(clientId) >= 70;
};

/**
 * Get theme documentation
 */
export const getThemeDocumentation = (): string => {
  return `
🎨 Healthcare App Theme System

Available Options:
- Client Themes: ${getAllClientIds().join(', ')}
- Theme Presets: ${getAvailablePresets().join(', ')}
- Style Variants: modern, classic, minimal, bold, elegant
- Color Intensities: subtle, standard, vibrant, high-contrast
- Border Radius: none, subtle, standard, rounded, pill
- Shadow Intensities: none, subtle, standard, elevated, dramatic

Usage Examples:
1. Switch client: CLIENT_ID = 'client2'
2. Apply preset: applyThemePreset('elegant')
3. Custom options: updateThemeOptions({ compactMode: true })
4. Generate custom: generateCustomTheme('client1', { style: 'bold' })

Accessibility: ${isThemeAccessible() ? '✅ Compliant' : '⚠️ Needs improvement'}
  `;
};

// Export current theme configuration
export const themeConfig = getThemeConfig();
export default themeConfig;

